import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneCompanies } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import GenerateDescription from "../utils/GenerateDescription";
import {
  ContactSection,
  SocialSection,
  GeoSection,
  VisibilitySection,
  DescriptionSection,
} from "../utils/AddSections";
import { FiImage } from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const company: any = getOneCompanies();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const company_id = company?.company_id;
  const [title, setTitle] = useState<any>(company?.title);
  const [thumbnail, setThumbnail] = useState<any>(company?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(company?.image_cdn_id);
  const [email, setEmail] = useState<any>(company?.email_address);
  const [payLink, setPayLink] = useState<any>(company?.pay_link);
  const [phoneNumber, setPhoneNumber] = useState<any>(company?.phone_number);
  const [whatsapp, setWhatsapp] = useState<any>(company?.whatsapp);
  const [facebook, setFacebook] = useState<any>(company?.facebook);
  const [twitter, setTwitter] = useState<any>(company?.twitter);
  const [instagram, setInstagram] = useState<any>(company?.instagram);
  const [youtube, setYoutube] = useState<any>(company?.youtube);
  const [hashtags, setHashtags] = useState<any>(company?.hashtag);
  const [googleMapCode, setGoogleMapCode] = useState<any>(
    company?.google_map_code
  );
  const [latitude, setLatitude] = useState<any>(company?.latitude);
  const [longitude, setLongitude] = useState<any>(company?.longitude);
  const [formattedAddress, setFormattedAddress] = useState<any>(
    company?.formatted_address
  );
  const [featured, setFeatured] = useState<any>(company?.featured);
  const [promoted, setPromoted] = useState<any>(
    company?.promoted ? company?.promoted : "no"
  );
  const [companyStatus, setCompanyStatus] = useState<any>(company?.status);
  const [description, setDescription] = useState<any>(company?.description);
  const [expo, setExpo] = useState<any>(company?.expo);
  const companyRoutes = "/data/company";
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const responses = (isError: any, message: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const updateOneCompanyHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : company?.country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title ? title.toLowerCase().replace(/\s/g, "_") : null,
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      phone_number: phoneNumber,
      email_address: email,
      pay_link: payLink,
      whatsapp: whatsapp,
      facebook: facebook,
      twitter: twitter,
      instagram: instagram,
      youtube: youtube,
      hashtag: hashtags,
      audio_adv: "none",
      google_map_code: googleMapCode,
      latitude: latitude,
      longitude: longitude,
      formatted_address: formattedAddress,
      country_short: "none",
      status: companyStatus,
      featured: featured,
      promoted: promoted,
      expo: expo,
    };

    const { message, status } = await updateOneContent({
      data_id: company_id,
      data: data,
      content: _TYPES_.COMPANY,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={companyRoutes}
      eyebrow="DoSee Data • Companies"
      title="Edit Company"
      subtitle={`Update a company in ${entry_label || "this destination"}.`}
      icon={<FaBuilding className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneCompanyHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="Logo or office photo."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.COMPANIES)}
        />
      </AddSection>

      <AddSection title="Basics" hint="Company name and address.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "formatted_address",
              title: "Formatted Address",
              type: "address",
              value: formattedAddress || "",
              onChange: (e: any) => {
                setFormattedAddress(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <ContactSection
        phone={phoneNumber}
        setPhone={setPhoneNumber}
        email={email}
        setEmail={setEmail}
        payLink={payLink}
        setPayLink={setPayLink}
        clearMessages={clearMessages}
      />

      <SocialSection
        whatsapp={whatsapp}
        setWhatsapp={setWhatsapp}
        facebook={facebook}
        setFacebook={setFacebook}
        twitter={twitter}
        setTwitter={setTwitter}
        instagram={instagram}
        setInstagram={setInstagram}
        youtube={youtube}
        setYoutube={setYoutube}
        clearMessages={clearMessages}
      />

      <GeoSection
        googleMapCode={googleMapCode}
        setGoogleMapCode={setGoogleMapCode}
        latitude={latitude}
        setLatitude={setLatitude}
        longitude={longitude}
        setLongitude={setLongitude}
        clearMessages={clearMessages}
      />

      <VisibilitySection
        statusId="company_status"
        setStatus={setCompanyStatus}
        setFeatured={setFeatured}
        setPromoted={setPromoted}
        setExpo={setExpo}
        status={companyStatus}
        featured={featured}
        promoted={promoted}
        expo={expo}
        clearMessages={clearMessages}
      />

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
